import { useMemo } from 'react';
import { AdminDTO } from '../types/admin.dto';
import { Types } from 'shared-lib';
import {
  dashboardDataApi,
  getPaginatedDriverDataApi,
  getAllRidersApi,
  getAppConfigs,
  getVehicleCategoryList
} from './customAPI';

// Tipos para o sistema de cache
interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live em milissegundos
  version: string;
  tags: string[];
}

interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
  storageType: 'memory' | 'localStorage' | 'sessionStorage';
  enableCompression: boolean;
  enableEncryption: boolean;
}

interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
}

class CacheService {
  private cache = new Map<string, CacheEntry>();
  private stats: CacheStats = { hits: 0, misses: 0, size: 0, hitRate: 0 };
  private config: CacheConfig;
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config?: Partial<CacheConfig>) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutos
      maxSize: 100, // 100 entradas
      storageType: 'memory',
      enableCompression: false, // Compression not implemented - keep disabled
      enableEncryption: false, // Encryption not implemented - keep disabled
      ...config
    };

    this.initializeFromStorage();
    this.startCleanupInterval();
  }

  /**
   * Inicializa o cache a partir do storage persistente
   */
  private initializeFromStorage(): void {
    if (this.config.storageType === 'memory') return;

    try {
      const storage = this.getStorage();
      const cacheData = storage.getItem('admin_cache');

      if (cacheData) {
        const parsed = JSON.parse(cacheData);
        Object.entries(parsed).forEach(([key, entry]) => {
          this.cache.set(key, entry as CacheEntry);
        });
      }
    } catch (error) {
      console.warn('Erro ao carregar cache do storage:', error);
    }
  }

  /**
   * Obtém o storage apropriado baseado na configuração
   */
  private getStorage(): Storage {
    return this.config.storageType === 'localStorage' ? localStorage : sessionStorage;
  }

  /**
   * Persiste o cache no storage
   */
  private persistToStorage(): void {
    if (this.config.storageType === 'memory') return;

    try {
      const storage = this.getStorage();
      const cacheObject = Object.fromEntries(this.cache.entries());
      storage.setItem('admin_cache', JSON.stringify(cacheObject));
    } catch (error) {
      console.warn('Erro ao persistir cache:', error);
    }
  }

  /**
   * Inicia o intervalo de limpeza automática
   */
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 60000); // Limpa a cada minuto
  }

  /**
   * Para o intervalo de limpeza
   */
  public stopCleanupInterval(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Destroys the cache service, cleaning up all resources
   */
  public destroy(): void {
    this.stopCleanupInterval();
    this.cache.clear();
  }

  /**
   * Verifica se uma entrada está expirada
   */
  private isExpired(entry: CacheEntry): boolean {
    return Date.now() > entry.timestamp + entry.ttl;
  }

  /**
   * Gera uma chave de cache baseada nos parâmetros
   */
  private generateKey(baseKey: string, params?: Record<string, unknown>): string {
    if (!params) return baseKey;

    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');

    return `${baseKey}:${btoa(sortedParams)}`;
  }

  /**
   * Comprime dados se habilitado (stub - real compression requires pako/lz-string)
   */
  private compress(data: unknown): string {
    return JSON.stringify(data);
  }

  /**
   * Descomprime dados se necessário
   */
  private decompress(data: string): unknown {
    return JSON.parse(data);
  }

  /**
   * Criptografa dados se habilitado (stub - real encryption requires crypto-js)
   */
  private encrypt(data: string): string {
    return data;
  }

  /**
   * Descriptografa dados se necessário
   */
  private decrypt(data: string): string {
    return data;
  }

  /**
   * Armazena dados no cache
   */
  public set<T>(
    key: string,
    data: T,
    options?: {
      ttl?: number;
      tags?: string[];
      params?: Record<string, unknown>;
      version?: string;
    }
  ): void {
    const finalKey = this.generateKey(key, options?.params);
    const ttl = options?.ttl || this.config.defaultTTL;
    const tags = options?.tags || [];
    const version = options?.version || '1.0';

    // Remove entradas antigas se o cache estiver cheio
    if (this.cache.size >= this.config.maxSize) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version,
      tags
    };

    this.cache.set(finalKey, entry);
    this.stats.size = this.cache.size;
    this.persistToStorage();
  }

  /**
   * Recupera dados do cache
   */
  public get<T>(
    key: string,
    options?: {
      params?: Record<string, unknown>;
      version?: string;
    }
  ): T | null {
    const finalKey = this.generateKey(key, options?.params);
    const entry = this.cache.get(finalKey);

    if (!entry) {
      this.stats.misses++;
      this.updateHitRate();
      return null;
    }

    // Verifica se expirou
    if (this.isExpired(entry)) {
      this.cache.delete(finalKey);
      this.stats.misses++;
      this.stats.size = this.cache.size;
      this.updateHitRate();
      this.persistToStorage();
      return null;
    }

    // Verifica versão se especificada
    if (options?.version && entry.version !== options.version) {
      this.cache.delete(finalKey);
      this.stats.misses++;
      this.stats.size = this.cache.size;
      this.updateHitRate();
      this.persistToStorage();
      return null;
    }

    this.stats.hits++;
    this.updateHitRate();
    return entry.data as T;
  }

  /**
   * Remove uma entrada específica do cache
   */
  public delete(key: string, params?: Record<string, unknown>): boolean {
    const finalKey = this.generateKey(key, params);
    const deleted = this.cache.delete(finalKey);

    if (deleted) {
      this.stats.size = this.cache.size;
      this.persistToStorage();
    }

    return deleted;
  }

  /**
   * Remove entradas por tags
   */
  public deleteByTags(tags: string[]): number {
    let deletedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (tags.some(tag => entry.tags.includes(tag))) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    if (deletedCount > 0) {
      this.stats.size = this.cache.size;
      this.persistToStorage();
    }

    return deletedCount;
  }

  /**
   * Limpa todo o cache
   */
  public clear(): void {
    this.cache.clear();
    this.stats = { hits: 0, misses: 0, size: 0, hitRate: 0 };
    this.persistToStorage();
  }

  /**
   * Remove entradas expiradas
   */
  public cleanup(): number {
    let cleanedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      this.stats.size = this.cache.size;
      this.persistToStorage();
    }

    return cleanedCount;
  }

  /**
   * Remove a entrada mais antiga
   */
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Atualiza a taxa de acerto
   */
  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
  }

  /**
   * Obtém estatísticas do cache
   */
  public getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Verifica se uma chave existe no cache
   */
  public has(key: string, params?: Record<string, unknown>): boolean {
    const finalKey = this.generateKey(key, params);
    const entry = this.cache.get(finalKey);
    return entry !== undefined && !this.isExpired(entry);
  }

  /**
   * Obtém todas as chaves do cache
   */
  public keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Obtém o tamanho atual do cache
   */
  public size(): number {
    return this.cache.size;
  }

  /**
   * Wrapper para requisições com cache automático
   */
  public async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    options?: {
      ttl?: number;
      tags?: string[];
      params?: Record<string, unknown>;
      version?: string;
      forceRefresh?: boolean;
    }
  ): Promise<T> {
    // Se forceRefresh for true, pula o cache
    if (!options?.forceRefresh) {
      const cached = this.get<T>(key, options);
      if (cached !== null) {
        return cached;
      }
    }

    try {
      const data = await fetchFn();
      this.set(key, data, options);
      return data;
    } catch (error) {
      // Se houver erro na requisição e temos dados em cache (mesmo expirados), retorna eles
      const staleData = this.cache.get(this.generateKey(key, options?.params));
      if (staleData) {
        console.warn('Usando dados em cache devido a erro na requisição:', error);
        return staleData.data as T;
      }
      throw error;
    }
  }
}

// Cache específico para dados administrativos
class AdminCacheService extends CacheService {
  constructor() {
    super({
      defaultTTL: 10 * 60 * 1000, // 10 minutos para dados admin
      maxSize: 200,
      storageType: 'sessionStorage',
      enableCompression: false // Disabled - no real compression implementation
    });
  }

  /**
   * Cache para dados do dashboard
   */
  public async getDashboardData(forceRefresh = false): Promise<AdminDTO.DashboardStats> {
    return this.getOrFetch<AdminDTO.DashboardStats>(
      'dashboard_stats',
      async () => {
        const response = await dashboardDataApi();
        // Backend wraps response as { message, data: { ...stats } }
        const rawData = response.data?.data ?? response.data;
        return rawData as AdminDTO.DashboardStats;
      },
      {
        ttl: 2 * 60 * 1000, // 2 minutos para dashboard
        tags: ['dashboard'],
        forceRefresh
      }
    );
  }

  /**
   * Cache para lista de drivers
   */
  public async getDrivers(
    params: { page: number; limit: number },
    forceRefresh = false
  ): Promise<AdminDTO.PaginatedResponse<AdminDTO.DriverUpdate>> {
    return this.getOrFetch<AdminDTO.PaginatedResponse<AdminDTO.DriverUpdate>>(
      'drivers_list',
      async () => {
        const response = await getPaginatedDriverDataApi(params);
        const total = response?.totalDrivers ?? 0;
        const totalPages = Math.max(1, Math.ceil(total / params.limit));
        const data = (response?.data ?? [])
          .filter(driver => driver?.id !== undefined && driver?.id !== null)
          .map(driver => ({
            id: Number(driver.id),
            firstName: driver.firstName,
            lastName: driver.lastName,
            email: driver.email,
            mobileNumber: driver.mobileNumber,
            status: driver.status,
            approved: driver.approved === Types.ApprovalStatus.APPROVED,
          }));
        return {
          data,
          pagination: {
            page: params.page,
            limit: params.limit,
            total,
            totalPages,
            hasNext: params.page < totalPages,
            hasPrev: params.page > 1,
          },
        };
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutos
        tags: ['drivers', 'lists'],
        params,
        forceRefresh
      }
    );
  }

  /**
   * Cache para lista de riders
   */
  public async getRiders(
    params: { page: number; limit: number },
    forceRefresh = false
  ): Promise<AdminDTO.PaginatedResponse<AdminDTO.RiderUpdate>> {
    return this.getOrFetch<AdminDTO.PaginatedResponse<AdminDTO.RiderUpdate>>(
      'riders_list',
      async () => {
        const response = await getAllRidersApi(params);
        return response as unknown as AdminDTO.PaginatedResponse<AdminDTO.RiderUpdate>;
      },
      {
        ttl: 5 * 60 * 1000,
        tags: ['riders', 'lists'],
        params,
        forceRefresh
      }
    );
  }

  /**
   * Cache para configurações da aplicação
   */
  public async getAppConfigs(forceRefresh = false): Promise<AdminDTO.AppConfig[]> {
    return this.getOrFetch<AdminDTO.AppConfig[]>(
      'app_configs',
      async () => {
        const response = await getAppConfigs();
        return response.data as AdminDTO.AppConfig[];
      },
      {
        ttl: 30 * 60 * 1000, // 30 minutos para configs
        tags: ['configs'],
        forceRefresh
      }
    );
  }

  /**
   * Cache para categorias de veículos
   */
  public async getCategories(forceRefresh = false): Promise<AdminDTO.Category[]> {
    return this.getOrFetch<AdminDTO.Category[]>(
      'vehicle_categories',
      async () => {
        const response = await getVehicleCategoryList();
        return response.data as AdminDTO.Category[];
      },
      {
        ttl: 15 * 60 * 1000, // 15 minutos
        tags: ['categories', 'vehicles'],
        forceRefresh
      }
    );
  }

  /**
   * Invalida cache relacionado a uma entidade específica
   */
  public invalidateEntity(entityType: 'drivers' | 'riders' | 'vehicles' | 'rides' | 'configs'): void {
    const tagMap = {
      drivers: ['drivers'],
      riders: ['riders'],
      vehicles: ['vehicles', 'categories'],
      rides: ['rides', 'dashboard'],
      configs: ['configs', 'dashboard']
    };

    this.deleteByTags(tagMap[entityType]);
  }

  /**
   * Invalida todo o cache de listas
   */
  public invalidateAllLists(): void {
    this.deleteByTags(['lists']);
  }

  /**
   * Pré-carrega dados essenciais
   */
  public async preloadEssentialData(): Promise<void> {
    try {
      await Promise.all([
        this.getDashboardData(),
        this.getAppConfigs(),
        this.getCategories()
      ]);
    } catch (error) {
      console.warn('Erro ao pré-carregar dados essenciais:', error);
    }
  }
}

// Instância singleton
export const cacheService = new CacheService();
export const adminCacheService = new AdminCacheService();

// Hook para React
export const useCache = () => {
  return useMemo(() => ({
    get: cacheService.get.bind(cacheService),
    set: cacheService.set.bind(cacheService),
    delete: cacheService.delete.bind(cacheService),
    clear: cacheService.clear.bind(cacheService),
    has: cacheService.has.bind(cacheService),
    getStats: cacheService.getStats.bind(cacheService),
    getOrFetch: cacheService.getOrFetch.bind(cacheService)
  }), []);
};

export const useAdminCache = () => {
  return useMemo(() => ({
    getDashboardData: adminCacheService.getDashboardData.bind(adminCacheService),
    getDrivers: adminCacheService.getDrivers.bind(adminCacheService),
    getRiders: adminCacheService.getRiders.bind(adminCacheService),
    getAppConfigs: adminCacheService.getAppConfigs.bind(adminCacheService),
    getCategories: adminCacheService.getCategories.bind(adminCacheService),
    invalidateEntity: adminCacheService.invalidateEntity.bind(adminCacheService),
    invalidateAllLists: adminCacheService.invalidateAllLists.bind(adminCacheService),
    preloadEssentialData: adminCacheService.preloadEssentialData.bind(adminCacheService)
  }), []);
};

export default cacheService;
